(() => {
  /** Position both control rows independently from the selected image's aspect ratio. */
  function artworkControlPositions({ width, height, bounds, size, gap, toolbarWidth, top = 12, bottom = height - 12 }) {
    const pad = 12;
    const clamp = (value, min, max) => Math.max(min, Math.min(value, max));
    // Keep two separate rows even when a tall/rotated image covers the whole stage.
    const topY = clamp(bounds.y - size - gap, top, bottom - size * 2 - gap);
    const lowerY = clamp(bounds.y + bounds.height + gap * 2, topY + size + gap, bottom - size);
    const center = bounds.x + bounds.width / 2;
    const rotateX = clamp(center - size / 2, pad, width - pad - size);
    let resizeX = clamp(bounds.x + bounds.width + gap, pad, width - pad - size);
    if (Math.abs(resizeX - rotateX) < size + gap) {
      resizeX = rotateX + size + gap <= width - pad - size ? rotateX + size + gap : rotateX - size - gap;
    }
    return {
      toolbar: { x: clamp(center - toolbarWidth / 2, pad, width - pad - toolbarWidth), y: topY },
      rotate: { x: rotateX, y: lowerY },
      resize: { x: resizeX, y: lowerY }
    };
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { artworkControlPositions };
  if (typeof customElements === 'undefined' || customElements.get('ld-artwork-controls')) return;

  customElements.define('ld-artwork-controls', class extends HTMLElement {
    connectedCallback() {
      this.stage = this.closest('.ldm-stage');
      this.logo = this.stage?.querySelector('.ldm-logo');
      if (!this.logo) return;
      this.toolbar = this.querySelector('.ldm-artwork-controls__toolbar');
      this.rotate = this.querySelector('.ldm-logo-rotate');
      this.resize = this.querySelector('.ldm-logo-resize');
      this.brush = this.querySelector('.ldm-logo-brush');
      this.schedule = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.updatePosition(); });
      };
      this.observer = new MutationObserver(this.schedule);
      this.observer.observe(this.logo, { attributes: true, subtree: true, attributeFilter: ['style', 'class', 'src'] });
      this.resizeObserver = new ResizeObserver(this.schedule);
      this.resizeObserver.observe(this.stage);
      this.resizeObserver.observe(this.logo);
      this.addEventListener('pointerdown', event => event.stopPropagation());
      this.schedule();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = 0;
    }

    updatePosition() {
      this.hidden = !this.logo.classList.contains('active') || !this.logo.classList.contains('selected');
      if (this.hidden) return;
      this.brush.hidden = this.logo.classList.contains('ldm-mode-text');
      const stage = this.stage.getBoundingClientRect();
      const logo = this.logo.getBoundingClientRect();
      if (!stage.width || !stage.height) return;
      const scaleX = this.stage.clientWidth / stage.width;
      const scaleY = this.stage.clientHeight / stage.height;
      const heading = this.stage.querySelector('.ldm-canvas-heading')?.getBoundingClientRect();
      const views = this.stage.querySelector('.ldm-views')?.getBoundingClientRect();
      const size = this.rotate.offsetWidth;
      const gap = parseFloat(getComputedStyle(this).getPropertyValue('--artwork-control-gap')) || 8;
      const top = heading?.height ? Math.max(12, (heading.bottom - stage.top) * scaleY + gap) : 12;
      const bottom = views?.height ? Math.min(this.stage.clientHeight - 12, (views.top - stage.top) * scaleY - gap) : this.stage.clientHeight - 12;
      const positions = artworkControlPositions({
        width: this.stage.clientWidth, height: this.stage.clientHeight,
        bounds: { x: (logo.left - stage.left) * scaleX, y: (logo.top - stage.top) * scaleY, width: logo.width * scaleX, height: logo.height * scaleY },
        size, gap, toolbarWidth: this.toolbar.offsetWidth, top, bottom
      });
      for (const name of ['toolbar', 'rotate', 'resize']) {
        this[name].style.left = `${positions[name].x}px`;
        this[name].style.top = `${positions[name].y}px`;
      }
    }
  });
})();
